import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/imageUrl";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Image as ImageIcon } from "lucide-react";

const CATEGORIES = [
  {
    key: "logo",
    label: "Logo",
    desc: "Single logo image — used in navbar and footer.",
    single: true,
    aspect: "aspect-square",
  },
  {
    key: "banner",
    label: "Home banner slides",
    desc: "Wide photos that scroll on the homepage. Use 1600×900px landscape images. Title shows on top.",
    aspect: "aspect-[16/9]",
    showTitle: true,
  },
  {
    key: "mill",
    label: "Mill (exterior / interior)",
    desc: "Photos of your rice mill building, signage, machinery.",
    aspect: "aspect-[4/3]",
  },
  {
    key: "process",
    label: "Milling process",
    desc: "Photos showing paddy cleaning, polishing, sorting.",
    aspect: "aspect-[4/3]",
  },
  {
    key: "packing",
    label: "Packing",
    desc: "Photos of bagged/packed rice, storage, dispatch.",
    aspect: "aspect-[4/3]",
  },
  {
    key: "quality",
    label: "Quality check",
    desc: "Photos of paddy/rice quality inspection.",
    aspect: "aspect-[4/3]",
  },
];

export default function AdminGallery() {
  const [gallery, setGallery] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/gallery");
      setGallery(data);
    } catch (err) {
      toast.error("Failed to load gallery");
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-gallery" className="mt-8 space-y-10">
      <div className="bg-[#f2ebd9] border border-stone-200 rounded-sm p-5 text-sm text-stone-700">
        Upload photos that tell your mill's story — these are shown on the homepage to build customer trust. 
        Recommended: 3–6 photos per category. Max 5 MB per image.
      </div>

      {CATEGORIES.map((cat) => (
        <CategorySection
          key={cat.key}
          category={cat}
          items={gallery[cat.key] || []}
          onChange={load}
        />
      ))}
    </div>
  );
}

function CategorySection({ category, items, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [titleEn, setTitleEn] = useState("");
  const [titleTe, setTitleTe] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({
        category: category.key,
        title_en: titleEn,
        title_te: titleTe,
      }).toString();
      await api.post(`/gallery?${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${category.label} image uploaded`);
      setTitleEn("");
      setTitleTe("");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this image?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Image deleted");
      onChange();
    } catch {
      toast.error("Delete failed");
    }
  };

  const canAddMore = !category.single || items.length === 0;

  return (
    <div data-testid={`gallery-section-${category.key}`}>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
            {category.single ? "Single image" : `${items.length} image${items.length === 1 ? "" : "s"}`}
          </div>
          <h3 className="text-xl font-semibold text-stone-900">{category.label}</h3>
          <p className="text-sm text-stone-500 mt-1">{category.desc}</p>
        </div>
        {canAddMore && (
          <button
            data-testid={`gallery-upload-${category.key}`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white px-4 py-2 rounded-sm text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : category.single && items.length ? "Replace image" : "Upload image"}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid={`gallery-input-${category.key}`}
        />
      </div>

      {category.showTitle && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-stone-50 border border-stone-200 rounded-sm">
          <div>
            <label className="text-[10px] tracking-wider uppercase text-stone-500 font-semibold">
              Overlay title (English) — optional
            </label>
            <input
              data-testid={`gallery-title-en-${category.key}`}
              value={titleEn}
              onChange={(e) => setTitleEn(e.target.value)}
              placeholder="e.g. Premium Sona Masuri at ₹50/kg"
              className="mt-1 w-full bg-white border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#274e13]"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-wider uppercase text-stone-500 font-semibold">
              Overlay title (Telugu) — optional
            </label>
            <input
              data-testid={`gallery-title-te-${category.key}`}
              value={titleTe}
              onChange={(e) => setTitleTe(e.target.value)}
              placeholder="ఉదా: ప్రీమియం సోనా మసూరి"
              className="mt-1 w-full bg-white border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#274e13] font-telugu"
            />
          </div>
          <div className="sm:col-span-2 text-[11px] text-stone-500">
            Fill these BEFORE clicking Upload. The text will appear on top of the image on the home page banner.
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-stone-300 rounded-sm p-10 text-center bg-stone-50">
          <ImageIcon size={28} className="mx-auto text-stone-400" />
          <p className="mt-3 text-sm text-stone-500">No images uploaded yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`gallery-item-${it.id}`}
              className={`relative group ${category.aspect} bg-stone-100 rounded-sm overflow-hidden border border-stone-200`}
            >
              <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover" />
              {category.showTitle && (it.title_en || it.title_te) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  {it.title_en && <div className="text-white text-xs font-semibold line-clamp-2">{it.title_en}</div>}
                  {it.title_te && <div className="text-stone-200 text-[11px] font-telugu line-clamp-1">{it.title_te}</div>}
                </div>
              )}
              <button
                data-testid={`gallery-delete-${it.id}`}
                onClick={() => onDelete(it.id)}
                className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-red-700 rounded-sm border border-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
